import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import {
  FormLabel,
  SxInput,
  Flex,
  Box,
  Text,
  Switch,
  FormControl,
  ModalBody,
  Button,
  ModalFooter,
  FormErrorMessage,
  TabletAndDesktop,
  Mobile,
} from '@stockx/ui';
import { EditListModalContext } from '@account/pages/Favorites/context';

/* 
Form
  validations: 
  cannot be the same
  cannot use the default list name
  cannot use special characters since it's sharable
  cannot have name that matches existing list
  
  disable submission:
  empty
  no changes to the form
  changes contain error
*/

interface EditFormInput {
  listName: string;
  public: boolean;
}

interface ListEditFormProps {
  currentData: EditFormInput;
  handleSubmit: (formInput: Partial<EditFormInput> | {}) => void;
  handleDeleteList: () => void;
  submitError: Error | string | null;
}

const ListEditForm = ({
  currentData,
  handleSubmit,
  handleDeleteList,
  submitError = '',
}: ListEditFormProps) => {
  const { t } = useTranslation();
  const { listName: curName = '', public: curSharingStatus } = currentData;
  const [query, setQuery] = useState(currentData.listName);
  const [isPublic, setIsPublic] = useState(currentData.public);
  const { onToggle } = useContext(EditListModalContext);
  const [error, setError] = useState(submitError as string);
  const handlelistNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setError('');

    const newName = e.target.value.trim();
    if (newName) {
      if (newName === t('all_favorites')) {
        setError(t('Cannot use the default list name')); // todo
      }
      // placeholder for validation
      // placeholder for duplication check
    }
  };

  const updateList = () => {
    const changes = {};
    if (curSharingStatus !== isPublic) {
      changes['public'] = isPublic;
    }

    if (curName !== query.trim()) {
      changes['name'] = query.trim();
    }

    if (!isEmpty(changes)) {
      handleSubmit(changes);
    }
  };

  const noChanges = useMemo(
    () => curSharingStatus === isPublic && curName === query.trim(),
    [curName, curSharingStatus, isPublic, query],
  );

  const disableFormSubmit = noChanges || !!error || !query?.trim(); // disable if there is a query

  return (
    <>
      <ModalBody paddingX={{ base: 0 }}>
        <FormControl isRequired isInvalid={!!error}>
          <FormLabel htmlFor="listname" marginY={1}>
            {t('List Name')}
          </FormLabel>
          <SxInput
            inputLabel={t('list_name_placeholder')}
            onChange={handlelistNameChange}
            value={query}
            maxLength={50}
          />
          <FormErrorMessage>{error}</FormErrorMessage>
        </FormControl>
        <Flex marginTop="7" justifyContent="space-between" alignItems="center">
          <Box>
            <Text fontSize="md" variant="medium">
              {t('Sharing')}
            </Text>
            <Text fontSize="sm" marginTop="1" maxWidth="61%">
              {t('list_edit_sharing_description')}
            </Text>
          </Box>
          <Switch
            data-testid="sharing-switch"
            isChecked={isPublic}
            onChange={() => setIsPublic((prev) => !prev)}
          />
        </Flex>
        <Button
          variant="unstyled"
          color="grey.700"
          marginTop="8"
          onClick={(e) => {
            e.preventDefault();
            handleDeleteList();
          }}
        >
          <Text
            as="span"
            textDecoration="underline"
            fontSize="sm"
            variant="medium"
            fontWeight="500"
          >
            {t('list_delete')}
          </Text>
        </Button>
      </ModalBody>
      <TabletAndDesktop>
        <ModalFooter>
          <Flex justifyContent="space-between" width="100%">
            <Button variant="secondary" size="md" onClick={onToggle}>
              {t('Close')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={updateList}
              isDisabled={disableFormSubmit}
            >
              {t('Save')}
            </Button>
          </Flex>
        </ModalFooter>
      </TabletAndDesktop>
      <Mobile>
        <Button
          marginTop="7"
          variant="primary"
          size="lg"
          width="100%"
          onClick={updateList}
          isDisabled={disableFormSubmit}
        >
          {t('Save')}
        </Button>
      </Mobile>
    </>
  );
};

export default ListEditForm;
